import {
  useState,
  useRef,
  useMemo,
  useEffect,
  useCallback,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type ChangeEvent,
  type FocusEvent,
  memo,
} from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '@/contexts/ThemeContext';
// moment-timezone re-exports moment with .tz() attached. We only call .tz() when the optional
// timeZoneId prop is set; otherwise the picker uses native browser-local Date methods exactly as
// it did before this prop existed (preserves backwards compatibility for ~30 callers).
import moment from 'moment-timezone';

// ---- constants ----

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const MONTH_NAMES_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const WEEKDAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const HOURS_24 = Array.from({ length: 24 }, (_, i) => i);
const MINUTES_60 = Array.from({ length: 60 }, (_, i) => i);
const SECONDS_60 = Array.from({ length: 60 }, (_, i) => i);

const HIDDEN_PORTAL_STYLE: CSSProperties = {
  position: 'fixed',
  visibility: 'hidden',
  top: '-9999px',
  left: '-9999px',
};

// ---- helpers ----

interface WindowWithOpera extends Window {
  opera?: string;
}

interface CalendarDay {
  day: number | '';
  isCurrentMonth: boolean;
}

function pad(num: number): string {
  return num < 10 ? '0' + num : num.toString();
}

// ---- Timezone-aware date operations ----
//
// When the picker is given a `timeZoneId` prop, every wall-clock read/write that previously
// used browser-local Date methods is routed through this object so it interprets values in
// the configured zone instead. When `timeZoneId` is undefined (the default), `dateOps` falls
// back to native Date methods — byte-for-byte identical to the pre-prop behavior. This keeps
// the change opt-in and risk-free for the ~30 existing callers that don't pass the prop.
//
// Only the `type === 'datetime'` paths consult `dateOps`. The `type === 'date'` path keeps
// its existing UTC-pinning convention (used for DOB-style fields where timezone is meaningless).
interface DateOps {
  getYear: (d: Date) => number;
  getMonth: (d: Date) => number;       // 0-indexed, matches Date.getMonth
  getDate: (d: Date) => number;
  getHours: (d: Date) => number;
  getMinutes: (d: Date) => number;
  getSeconds: (d: Date) => number;
  makeDate: (
    year: number,
    monthIndex: number,
    day: number,
    hours?: number,
    minutes?: number,
    seconds?: number,
  ) => Date;
}

// Exported for unit testing.
export function createDateOps(timeZoneId?: string): DateOps {
  if (!timeZoneId) {
    return {
      getYear: (d) => d.getFullYear(),
      getMonth: (d) => d.getMonth(),
      getDate: (d) => d.getDate(),
      getHours: (d) => d.getHours(),
      getMinutes: (d) => d.getMinutes(),
      getSeconds: (d) => d.getSeconds(),
      makeDate: (y, m, day, h = 0, mi = 0, s = 0) => new Date(y, m, day, h, mi, s),
    };
  }
  return {
    getYear: (d) => moment.tz(d, timeZoneId).year(),
    getMonth: (d) => moment.tz(d, timeZoneId).month(),
    getDate: (d) => moment.tz(d, timeZoneId).date(),
    getHours: (d) => moment.tz(d, timeZoneId).hour(),
    getMinutes: (d) => moment.tz(d, timeZoneId).minute(),
    getSeconds: (d) => moment.tz(d, timeZoneId).second(),
    // Chained setters (vs. object form) avoid moment's day/date key ambiguity and force
    // millisecond=0 so re-saves don't drift on the sub-second.
    makeDate: (y, m, day, h = 0, mi = 0, s = 0) =>
      moment
        .tz(timeZoneId)
        .year(y)
        .month(m)
        .date(day)
        .hour(h)
        .minute(mi)
        .second(s)
        .millisecond(0)
        .toDate(),
  };
}

function checkIfMobile(): boolean {
  return window.innerWidth <= 1024;
}

function checkIfAndroid(): boolean {
  const win = window as unknown as WindowWithOpera;
  const userAgent = navigator.userAgent || navigator.vendor || win.opera || '';
  return /android/i.test(userAgent);
}

// ---- types ----

type PickerType = 'date' | 'datetime';
type ViewType = 'calendar' | 'month-selector' | 'year-selector' | 'time';

export interface DateTimePickerProps {
  value: Date | null;
  type?: PickerType;
  format?: string;
  valueType?: string;
  showSecond?: boolean;
  clearable?: boolean;
  appendToBody?: boolean;
  popupClass?: string;
  open?: boolean;
  inputAttr?: Record<string, any>;
  disabled?: boolean;
  defaultYear?: number | null;
  /**
   * Optional IANA time zone (e.g. "America/Chicago", "UTC"). When set, the picker interprets
   * typed/selected wall-clock values in this zone and renders the stored Instant in this zone.
   * When undefined (default), the picker uses the browser's local zone — identical to the
   * pre-prop behavior. Only affects `type === 'datetime'`; `type === 'date'` keeps its existing
   * UTC-pinning convention.
   */
  timeZoneId?: string;
  onChange?: (value: Date | null) => void;
  onConfirm?: () => void;
  onClear?: () => void;
  onOpen?: () => void;
  onClose?: () => void;
  onFocus?: () => void;
  onEnteringTextMode?: () => void;
  onKeyDown?: (event: ReactKeyboardEvent<HTMLInputElement>) => void;
  onOpenChange?: (open: boolean) => void;
}

// ---- component ----

export const DateTimePicker = memo(function DateTimePicker({
  value,
  type = 'date',
  format = 'DD MMM YYYY',
  valueType = 'date',
  showSecond = false,
  clearable = true,
  appendToBody = false,
  popupClass = '',
  open: openProp = false,
  inputAttr = {},
  disabled = false,
  defaultYear = null,
  timeZoneId,
  onChange,
  onConfirm,
  onClear,
  onOpen,
  onClose,
  onFocus,
  onEnteringTextMode,
  onKeyDown,
  onOpenChange,
}: DateTimePickerProps) {
  const { theme: currentTheme } = useTheme();

  // dateOps abstracts every wall-clock read/write so it can be re-routed through moment-tz
  // when `timeZoneId` is set. When unset, dateOps wraps native Date methods 1:1.
  const dateOps = useMemo(() => createDateOps(timeZoneId), [timeZoneId]);

  // ---- state ----
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState<ViewType>('calendar');
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedHour, setSelectedHour] = useState(0);
  const [selectedMinute, setSelectedMinute] = useState(0);
  const [selectedSecond, setSelectedSecond] = useState(0);
  const [currentMonth, setCurrentMonth] = useState(0);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [yearRangeStart, setYearRangeStart] = useState(new Date().getFullYear() - 5);
  const [portalStyle, setPortalStyle] = useState<CSSProperties>(HIDDEN_PORTAL_STYLE);
  const [inputText, setInputText] = useState('');
  const [isEditingText, setIsEditingText] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [keyboardVisible, setKeyboardVisible] = useState(false);

  // ---- refs ----
  const controlRef = useRef<HTMLDivElement>(null);
  const portalRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const justOpenedRef = useRef(false);
  const transitioningToTextModeRef = useRef(false);
  const isAndroidRef = useRef(false);

  // Refs that mirror state for use in document/window event listeners (stale-closure prevention)
  const isOpenRef = useRef(isOpen);
  const isEditingTextRef = useRef(isEditingText);
  const selectedDateRef = useRef(selectedDate);
  const selectedHourRef = useRef(selectedHour);
  const selectedMinuteRef = useRef(selectedMinute);
  const selectedSecondRef = useRef(selectedSecond);
  const currentMonthRef = useRef(currentMonth);
  const currentYearRef = useRef(currentYear);
  const viewRef = useRef(view);
  const isMobileRef = useRef(isMobile);

  // Keep refs in sync
  useEffect(() => { isOpenRef.current = isOpen; }, [isOpen]);
  useEffect(() => { isEditingTextRef.current = isEditingText; }, [isEditingText]);
  useEffect(() => { selectedDateRef.current = selectedDate; }, [selectedDate]);
  useEffect(() => { selectedHourRef.current = selectedHour; }, [selectedHour]);
  useEffect(() => { selectedMinuteRef.current = selectedMinute; }, [selectedMinute]);
  useEffect(() => { selectedSecondRef.current = selectedSecond; }, [selectedSecond]);
  useEffect(() => { currentMonthRef.current = currentMonth; }, [currentMonth]);
  useEffect(() => { currentYearRef.current = currentYear; }, [currentYear]);
  useEffect(() => { viewRef.current = view; }, [view]);
  useEffect(() => { isMobileRef.current = isMobile; }, [isMobile]);

  // Callback refs to avoid stale closures in callbacks passed to parent
  const onChangeRef = useRef(onChange);
  const onConfirmRef = useRef(onConfirm);
  const onClearRef = useRef(onClear);
  const onCloseRef = useRef(onClose);
  const onOpenRef = useRef(onOpen);
  const onFocusRef = useRef(onFocus);
  const onEnteringTextModeRef = useRef(onEnteringTextMode);
  const onKeyDownRef = useRef(onKeyDown);
  const onOpenChangeRef = useRef(onOpenChange);

  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);
  useEffect(() => { onConfirmRef.current = onConfirm; }, [onConfirm]);
  useEffect(() => { onClearRef.current = onClear; }, [onClear]);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
  useEffect(() => { onOpenRef.current = onOpen; }, [onOpen]);
  useEffect(() => { onFocusRef.current = onFocus; }, [onFocus]);
  useEffect(() => { onEnteringTextModeRef.current = onEnteringTextMode; }, [onEnteringTextMode]);
  useEffect(() => { onKeyDownRef.current = onKeyDown; }, [onKeyDown]);
  useEffect(() => { onOpenChangeRef.current = onOpenChange; }, [onOpenChange]);

  // ---- formatting helpers (stable, no deps on state) ----

  const formatDate = useCallback((date: Date | null): string => {
    if (!date) return '';
    const d = new Date(date);

    if (type === 'datetime') {
      const day = pad(dateOps.getDate(d));
      const month = MONTH_NAMES_SHORT[dateOps.getMonth(d)];
      const year = dateOps.getYear(d);
      const hours = pad(dateOps.getHours(d));
      const minutes = pad(dateOps.getMinutes(d));
      const seconds = pad(dateOps.getSeconds(d));
      if (showSecond) {
        return `${day} ${month} ${year} ${hours}:${minutes}:${seconds}`;
      }
      return `${day} ${month} ${year} ${hours}:${minutes}`;
    }

    // Date-only: use UTC to avoid timezone shift (unchanged — date-only doesn't honor timeZoneId)
    const day = pad(d.getUTCDate());
    const month = MONTH_NAMES_SHORT[d.getUTCMonth()];
    const year = d.getUTCFullYear();
    return `${day} ${month} ${year}`;
  }, [type, showSecond, dateOps]);

  const parseDate = useCallback((str: string): Date | null => {
    if (!str) return null;

    const datePattern = /(\d{1,2})\s+(\w{3})\s+(\d{4})/i;
    const dateMatch = str.match(datePattern);

    if (dateMatch) {
      const day = parseInt(dateMatch[1]);
      const monthStr = dateMatch[2];
      const year = parseInt(dateMatch[3]);

      const monthIndex = MONTH_NAMES_SHORT.findIndex(
        (m) => m.toLowerCase() === monthStr.toLowerCase()
      );

      if (monthIndex >= 0) {
        let hours = 0;
        let minutes = 0;
        let seconds = 0;

        const timeStr = str.substring(dateMatch[0].length).trim();

        if (timeStr) {
          const timeWithSecondsPattern = /^(\d{1,2}):(\d{2}):(\d{2})$/;
          const timeWithSecondsMatch = timeStr.match(timeWithSecondsPattern);

          if (timeWithSecondsMatch) {
            hours = parseInt(timeWithSecondsMatch[1]);
            minutes = parseInt(timeWithSecondsMatch[2]);
            seconds = parseInt(timeWithSecondsMatch[3]);
          } else {
            const timeWithoutSecondsPattern = /^(\d{1,2}):(\d{2})$/;
            const timeWithoutSecondsMatch = timeStr.match(timeWithoutSecondsPattern);

            if (timeWithoutSecondsMatch) {
              hours = parseInt(timeWithoutSecondsMatch[1]);
              minutes = parseInt(timeWithoutSecondsMatch[2]);
              seconds = 0;
            } else {
              const timeCompactPattern = /^(\d{4})$/;
              const timeCompactMatch = timeStr.match(timeCompactPattern);

              if (timeCompactMatch) {
                const timeDigits = timeCompactMatch[1];
                hours = parseInt(timeDigits.substring(0, 2));
                minutes = parseInt(timeDigits.substring(2, 4));
                seconds = 0;
              }
            }
          }

          if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59 || seconds < 0 || seconds > 59) {
            return null;
          }
        }

        let date: Date;
        if (type === 'datetime') {
          date = dateOps.makeDate(year, monthIndex, day, hours, minutes, seconds);
          // Validate round-trip in the operating zone — guards against invalid wall-clock
          // values (e.g. 31 Feb) that the constructor would silently roll forward.
          if (
            dateOps.getDate(date) === day &&
            dateOps.getMonth(date) === monthIndex &&
            dateOps.getYear(date) === year
          ) {
            return date;
          }
        } else {
          date = new Date(Date.UTC(year, monthIndex, day));
          if (date.getUTCDate() === day && date.getUTCMonth() === monthIndex && date.getUTCFullYear() === year) {
            return date;
          }
        }
      }
    }

    const attempt = new Date(str);
    if (!isNaN(attempt.getTime())) {
      return attempt;
    }

    return null;
  }, [type, dateOps]);

  // ---- initialize from value ----

  const initializeFromValue = useCallback(() => {
    if (value) {
      const d = new Date(value);
      setSelectedDate(d);
      if (type === 'datetime') {
        setSelectedHour(dateOps.getHours(d));
        setSelectedMinute(dateOps.getMinutes(d));
        setSelectedSecond(dateOps.getSeconds(d));
        setCurrentMonth(dateOps.getMonth(d));
        setCurrentYear(dateOps.getYear(d));
      } else {
        setSelectedHour(0);
        setSelectedMinute(0);
        setSelectedSecond(0);
        setCurrentMonth(d.getUTCMonth());
        setCurrentYear(d.getUTCFullYear());
      }
    } else {
      const now = new Date();
      setSelectedDate(null);
      if (type === 'datetime') {
        setCurrentMonth(dateOps.getMonth(now));
        setCurrentYear(defaultYear || dateOps.getYear(now));
        setSelectedHour(dateOps.getHours(now));
        setSelectedMinute(dateOps.getMinutes(now));
        setSelectedSecond(dateOps.getSeconds(now));
      } else {
        setCurrentMonth(now.getMonth());
        setCurrentYear(defaultYear || now.getFullYear());
        setSelectedHour(now.getHours());
        setSelectedMinute(now.getMinutes());
        setSelectedSecond(now.getSeconds());
      }
    }
  }, [value, type, defaultYear, dateOps]);

  // Update yearRangeStart whenever currentYear changes
  useEffect(() => {
    setYearRangeStart(currentYear - 5);
  }, [currentYear]);

  // ---- computed values ----

  const calendarDays = useMemo((): CalendarDay[] => {
    const firstDay = new Date(currentYear, currentMonth, 1);
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    const firstDayOfWeek = firstDay.getDay();
    const daysInMonth = lastDay.getDate();

    const days: CalendarDay[] = [];

    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push({ day: '', isCurrentMonth: false });
    }

    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ day: i, isCurrentMonth: true });
    }

    const totalCells = Math.ceil(days.length / 7) * 7;
    for (let i = days.length; i < totalCells; i++) {
      days.push({ day: '', isCurrentMonth: false });
    }

    return days;
  }, [currentYear, currentMonth]);

  const monthName = useMemo(() => MONTH_NAMES[currentMonth], [currentMonth]);

  const yearRange = useMemo(() => {
    const years: number[] = [];
    for (let i = 0; i < 12; i++) {
      years.push(yearRangeStart + i);
    }
    return years;
  }, [yearRangeStart]);

  const portalClasses = useMemo(() => {
    let classes = popupClass || currentTheme;
    if (isAndroidRef.current) {
      classes += ' android-material';
    }
    if (currentTheme && !classes.includes(currentTheme)) {
      classes += ' ' + currentTheme;
    }
    return classes.trim();
  }, [popupClass, currentTheme]);

  const wrapperClass = useMemo(() => {
    let classes = 'datetime-picker-wrapper';
    if (currentTheme) classes += ' ' + currentTheme;
    if (disabled) classes += ' datetime-picker-disabled';
    return classes;
  }, [currentTheme, disabled]);

  const isInputReadonly = useMemo(() => {
    return isMobile && !isOpen && !isEditingText;
  }, [isMobile, isOpen, isEditingText]);

  const displayValue = useMemo(() => {
    if (isEditingText) {
      return inputText;
    }
    if (isOpen && selectedDate) {
      return formatDate(selectedDate);
    }
    if (!value) return '';
    return formatDate(value);
  }, [isEditingText, inputText, isOpen, selectedDate, value, formatDate]);

  // ---- portal positioning ----

  const positionPortal = useCallback((controlElement: HTMLElement, retryCount = 0) => {
    const rect = controlElement.getBoundingClientRect();

    const isValidRect =
      rect.width > 0 &&
      rect.height > 0 &&
      !(rect.top === 0 && rect.bottom === 0 && rect.left === 0 && rect.right === 0);

    if (!isValidRect) {
      if (retryCount < 3) {
        requestAnimationFrame(() => {
          if (isOpenRef.current && controlElement) {
            positionPortal(controlElement, retryCount + 1);
          }
        });
      }
      return;
    }

    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    if (rect.bottom < -100 || rect.top > viewportHeight + 100) {
      // Element scrolled out of view - close picker
      closePicker();
      return;
    }

    const mobile = isMobileRef.current;
    const dropdownHeight = mobile ? 280 : 320;
    const dropdownWidth = mobile ? 280 : 320;
    const padding = 10;

    let leftPos = rect.left;
    const rightEdge = leftPos + dropdownWidth;

    if (rightEdge > viewportWidth - padding) {
      leftPos = viewportWidth - dropdownWidth - padding;
    }
    leftPos = Math.max(padding, leftPos);

    const spaceBelow = viewportHeight - rect.bottom - padding;
    const spaceAbove = rect.top - padding;

    let topPos: number;
    let useAbove = false;

    const actualHeight = portalRef.current ? portalRef.current.getBoundingClientRect().height : 0;
    const pickerHeight = actualHeight > 100 ? actualHeight : dropdownHeight;

    if (spaceBelow < pickerHeight && spaceAbove > spaceBelow && spaceAbove > 100) {
      useAbove = true;
      let positionHeight = Math.min(pickerHeight, Math.max(200, spaceAbove));
      topPos = rect.top - positionHeight - 2;

      if (topPos < padding) {
        positionHeight = rect.top - padding - 2;
        topPos = padding;
        if (positionHeight < 150) {
          useAbove = false;
        }
      }
    }

    if (!useAbove) {
      topPos = rect.bottom + 2;
    }

    setPortalStyle({
      position: 'fixed',
      top: topPos! + 'px',
      left: leftPos + 'px',
      width: dropdownWidth + 'px',
      zIndex: 999999,
      transform: 'translate3d(0, 0, 0)',
      WebkitTransform: 'translate3d(0, 0, 0)',
      willChange: 'transform',
      visibility: 'visible',
    } as CSSProperties);
  }, []);

  const updatePosition = useCallback(() => {
    if (isOpenRef.current && controlRef.current) {
      positionPortal(controlRef.current);
    }
  }, [positionPortal]);

  // ---- closePicker ----

  const autoSave = useCallback(() => {
    const sd = selectedDateRef.current;
    if (!sd) return;

    let finalDate: Date;

    if (type === 'datetime') {
      const secondsToUse = showSecond ? selectedSecondRef.current : 0;
      finalDate = dateOps.makeDate(
        dateOps.getYear(sd),
        dateOps.getMonth(sd),
        dateOps.getDate(sd),
        selectedHourRef.current,
        selectedMinuteRef.current,
        secondsToUse
      );
    } else {
      finalDate = new Date(sd);
    }

    onChangeRef.current?.(finalDate);
    onConfirmRef.current?.();
  }, [type, showSecond, dateOps]);

  const closePicker = useCallback(() => {
    // Auto-save if we're in time view and have a selected date
    if (viewRef.current === 'time' && selectedDateRef.current) {
      autoSave();
    }

    setIsOpen(false);
    setView('calendar');
    setPortalStyle(HIDDEN_PORTAL_STYLE);
    onOpenChangeRef.current?.(false);
    onCloseRef.current?.();
  }, [autoSave]);

  // Store closePicker in a ref so document click handler always has the latest version
  const closePickerRef = useRef(closePicker);
  useEffect(() => { closePickerRef.current = closePicker; }, [closePicker]);

  // Store positionPortal in a ref for the same reason
  const positionPortalRef = useRef(positionPortal);
  useEffect(() => { positionPortalRef.current = positionPortal; }, [positionPortal]);

  const updatePositionRef = useRef(updatePosition);
  useEffect(() => { updatePositionRef.current = updatePosition; }, [updatePosition]);

  // ---- scrolling helpers ----

  const scrollToCurrentTime = useCallback(() => {
    requestAnimationFrame(() => {
      const portal = portalRef.current;
      if (!portal) return;

      const hourItems = portal.querySelectorAll('.time-column-hour .column-item');
      if (hourItems[selectedHourRef.current]) {
        hourItems[selectedHourRef.current].scrollIntoView({ block: 'center' });
      }

      const minuteItems = portal.querySelectorAll('.time-column-minute .column-item');
      if (minuteItems[selectedMinuteRef.current]) {
        minuteItems[selectedMinuteRef.current].scrollIntoView({ block: 'center' });
      }

      if (showSecond) {
        const secondItems = portal.querySelectorAll('.time-column-second .column-item');
        if (secondItems[selectedSecondRef.current]) {
          secondItems[selectedSecondRef.current].scrollIntoView({ block: 'center' });
        }
      }
    });
  }, [showSecond]);

  const scrollSelectedToCenter = useCallback((columnType: 'hour' | 'minute' | 'second') => {
    const portal = portalRef.current;
    if (!portal) return;

    if (columnType === 'hour') {
      const hourItems = portal.querySelectorAll('.time-column-hour .column-item');
      if (hourItems[selectedHourRef.current]) {
        hourItems[selectedHourRef.current].scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    } else if (columnType === 'minute') {
      const minuteItems = portal.querySelectorAll('.time-column-minute .column-item');
      if (minuteItems[selectedMinuteRef.current]) {
        minuteItems[selectedMinuteRef.current].scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    } else if (columnType === 'second') {
      const secondItems = portal.querySelectorAll('.time-column-second .column-item');
      if (secondItems[selectedSecondRef.current]) {
        secondItems[selectedSecondRef.current].scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    }
  }, []);

  // ---- openPicker ----

  const openPicker = useCallback((force?: boolean) => {
    if (disabled) return;

    if (isEditingTextRef.current && !force) return;

    // On desktop, enable text editing when picker opens
    if (!isMobileRef.current) {
      setIsEditingText(true);
      setInputText(value ? formatDate(value) : '');
    }

    setIsOpen(true);
    setView('calendar');
    justOpenedRef.current = true;
    onOpenChangeRef.current?.(true);
    onOpenRef.current?.();

    // Position after render
    requestAnimationFrame(() => {
      if (controlRef.current) {
        positionPortalRef.current(controlRef.current);
      }

      // Reposition after content fully renders
      requestAnimationFrame(() => {
        if (isOpenRef.current && controlRef.current) {
          positionPortalRef.current(controlRef.current);
        }
      });
    });

    setTimeout(() => {
      justOpenedRef.current = false;
    }, 200);
  }, [disabled, value, formatDate]);

  // ---- confirm ----

  const confirm = useCallback(() => {
    autoSave();
    // closePicker without auto-save (already called)
    setIsOpen(false);
    setView('calendar');
    setPortalStyle(HIDDEN_PORTAL_STYLE);
    onOpenChangeRef.current?.(false);
    onCloseRef.current?.();
  }, [autoSave]);

  // ---- watch: open prop ----

  const openPickerRef = useRef(openPicker);
  useEffect(() => { openPickerRef.current = openPicker; }, [openPicker]);

  useEffect(() => {
    if (openProp !== isOpenRef.current) {
      if (openProp) {
        openPickerRef.current(true);
      } else {
        closePickerRef.current();
      }
    }
  }, [openProp]);

  // ---- watch: value prop ----

  useEffect(() => {
    if (!isOpenRef.current) {
      initializeFromValue();
    }
    if (!isEditingTextRef.current) {
      setInputText(value ? formatDate(value) : '');
    }
  }, [value, initializeFromValue, formatDate]);

  // ---- mount effects ----

  useEffect(() => {
    setIsMobile(checkIfMobile());
    isAndroidRef.current = checkIfAndroid();
    initializeFromValue();
    setInputText(value ? formatDate(value) : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Document click handler (click-outside)
  useEffect(() => {
    const handleDocumentClick = (event: Event) => {
      if (!isOpenRef.current || justOpenedRef.current) return;

      const target = event.target as HTMLElement;
      const portal = portalRef.current;
      const control = controlRef.current;
      const input = inputRef.current;

      if (input && (target === input || input.contains(target))) return;

      if (
        (!portal || !portal.contains(target)) &&
        (!control || !control.contains(target))
      ) {
        closePickerRef.current();
      }
    };

    // Capture phase: sibling widgets (e.g. TimePicker) stopPropagation() on
    // their control clicks, which would otherwise never reach a bubble-phase
    // document listener and leave this picker stuck open alongside them.
    document.addEventListener('click', handleDocumentClick, true);
    return () => {
      document.removeEventListener('click', handleDocumentClick, true);
    };
  }, []);

  // Window resize handler (debounced mobile check + rAF-throttled position)
  useEffect(() => {
    let mobileTimer: ReturnType<typeof setTimeout> | null = null;
    let rafId = 0;
    const handleResize = () => {
      // Debounce mobile check
      if (mobileTimer) clearTimeout(mobileTimer);
      mobileTimer = setTimeout(() => setIsMobile(checkIfMobile()), 150);
      // rAF-throttle position update
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        updatePositionRef.current();
      });
    };

    window.addEventListener('resize', handleResize);
    return () => {
      if (mobileTimer) clearTimeout(mobileTimer);
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Scroll handler (capture phase, rAF-throttled)
  // Ignores scroll events originating from within the picker portal itself
  // (e.g. time column scrolling) to avoid unwanted repositioning.
  useEffect(() => {
    let rafId = 0;
    const handleScroll = (event: Event) => {
      if (portalRef.current && portalRef.current.contains(event.target as Node)) return;

      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        updatePositionRef.current();
      });
    };

    window.addEventListener('scroll', handleScroll, true);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, []);

  // Mobile viewport resize handler (keyboard detection)
  useEffect(() => {
    if (!isMobile) return;

    const handleViewportResize = () => {
      if (window.visualViewport) {
        const viewportHeight = window.visualViewport.height;
        const windowHeight = window.innerHeight;
        const keyboardHeight = windowHeight - viewportHeight;

        setKeyboardVisible(keyboardHeight > 100);

        if (keyboardHeight > 100 && isOpenRef.current) {
          updatePositionRef.current();
        }
      }
    };

    window.visualViewport?.addEventListener('resize', handleViewportResize);
    return () => {
      window.visualViewport?.removeEventListener('resize', handleViewportResize);
    };
  }, [isMobile]);

  // ---- day helpers ----

  const isToday = useCallback((day: CalendarDay): boolean => {
    if (!day.isCurrentMonth || !day.day) return false;
    const today = new Date();
    // "Today" in the operating zone — when timeZoneId is set, this is the date in that zone,
    // which can legitimately differ from the browser-local date by 1 day around midnight.
    return (
      day.day === dateOps.getDate(today) &&
      currentMonth === dateOps.getMonth(today) &&
      currentYear === dateOps.getYear(today)
    );
  }, [currentMonth, currentYear, dateOps]);

  const isSelectedDay = useCallback(
    (day: CalendarDay): boolean => {
      if (!selectedDate || !day.isCurrentMonth || !day.day) return false;
      if (type === 'datetime') {
        return (
          day.day === dateOps.getDate(selectedDate) &&
          currentMonth === dateOps.getMonth(selectedDate) &&
          currentYear === dateOps.getYear(selectedDate)
        );
      }
      return (
        day.day === selectedDate.getUTCDate() &&
        currentMonth === selectedDate.getUTCMonth() &&
        currentYear === selectedDate.getUTCFullYear()
      );
    },
    [selectedDate, currentMonth, currentYear, type, dateOps]
  );

  const isCurrentMonthHighlight = useCallback((monthIndex: number): boolean => {
    const today = new Date();
    return monthIndex === dateOps.getMonth(today) && currentYear === dateOps.getYear(today);
  }, [currentYear, dateOps]);

  const isSelectedMonthHighlight = useCallback(
    (monthIndex: number): boolean => monthIndex === currentMonth,
    [currentMonth]
  );

  const isCurrentYearHighlight = useCallback((year: number): boolean => {
    const today = new Date();
    return year === dateOps.getYear(today);
  }, [dateOps]);

  const isSelectedYearHighlight = useCallback(
    (year: number): boolean => year === currentYear,
    [currentYear]
  );

  // ---- syncPickerFromInput ----

  const syncPickerFromInput = useCallback(
    (text: string) => {
      const parsed = parseDate(text);
      if (parsed) {
        setSelectedDate(parsed);
        if (type === 'datetime') {
          setSelectedHour(dateOps.getHours(parsed));
          setSelectedMinute(dateOps.getMinutes(parsed));
          setSelectedSecond(dateOps.getSeconds(parsed));
          setCurrentMonth(dateOps.getMonth(parsed));
          setCurrentYear(dateOps.getYear(parsed));
        } else {
          setCurrentMonth(parsed.getUTCMonth());
          setCurrentYear(parsed.getUTCFullYear());
        }
      }
    },
    [parseDate, type, dateOps]
  );

  // ---- parseAndSetDate ----

  const parseAndSetDate = useCallback(() => {
    const text = inputRef.current?.value ?? '';
    if (!text || text.trim() === '') {
      onChangeRef.current?.(null);
      onClearRef.current?.();
      return;
    }

    let parsed = parseDate(text);
    if (parsed) {
      if (type === 'datetime' && !showSecond) {
        // Re-build with seconds zeroed, preserving the user's intended wall-clock in the zone.
        parsed = dateOps.makeDate(
          dateOps.getYear(parsed),
          dateOps.getMonth(parsed),
          dateOps.getDate(parsed),
          dateOps.getHours(parsed),
          dateOps.getMinutes(parsed),
          0
        );
      }

      setSelectedDate(parsed);
      if (type === 'datetime') {
        setSelectedHour(dateOps.getHours(parsed));
        setSelectedMinute(dateOps.getMinutes(parsed));
        setSelectedSecond(dateOps.getSeconds(parsed));
        setCurrentMonth(dateOps.getMonth(parsed));
        setCurrentYear(dateOps.getYear(parsed));
      } else {
        setCurrentMonth(parsed.getUTCMonth());
        setCurrentYear(parsed.getUTCFullYear());
      }

      onChangeRef.current?.(parsed);
      onConfirmRef.current?.();
      setInputText(formatDate(parsed));
    } else {
      setInputText(value ? formatDate(value) : '');
    }
  }, [parseDate, type, showSecond, formatDate, value, dateOps]);

  // ---- event handlers ----

  const handleControlClick = useCallback(
    (_event: ReactMouseEvent) => {
      if (!isOpen && !isEditingText) {
        openPicker();
      }
    },
    [isOpen, isEditingText, openPicker]
  );

  const handleKeydown = useCallback(
    (event: ReactKeyboardEvent) => {
      if (isEditingText) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        closePicker();
      } else if (event.key === 'Tab') {
        event.stopPropagation();
      }
    },
    [isEditingText, closePicker]
  );

  const handleInputMousedown = useCallback(() => {
    if (!isOpen && !disabled) {
      justOpenedRef.current = true;
      setTimeout(() => {
        if (!isOpenRef.current) {
          justOpenedRef.current = false;
        }
      }, 300);
    }
  }, [isOpen, disabled]);

  const handleInputFocus = useCallback(
    (_event: FocusEvent<HTMLInputElement>) => {
      // Our own refocus when switching to text mode is not a user focus. Telling the
      // parent would let it reopen the picker on top of text editing (Notes does,
      // via its onFocus timer), and each tap on the input would then close/reopen it.
      if (transitioningToTextModeRef.current) return;

      onFocusRef.current?.();

      if (justOpenedRef.current) {
        if (isMobile && inputRef.current) {
          inputRef.current.blur();
        }
        return;
      }

      if (isMobile && !isOpen) {
        if (inputRef.current) {
          inputRef.current.blur();
        }
        return;
      }

      if (!isMobile || isOpen) {
        setIsEditingText(true);
      }
    },
    [isMobile, isOpen]
  );

  const handleInputBlur = useCallback(() => {
    if (justOpenedRef.current) return;
    if (transitioningToTextModeRef.current) return;

    parseAndSetDate();
    setIsEditingText(false);

    if (isMobileRef.current) {
      setTimeout(() => {
        if (!isOpenRef.current && !isEditingTextRef.current) {
          onCloseRef.current?.();
        }
      }, 150);
    }
  }, [parseAndSetDate]);

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const newText = event.target.value;
      setInputText(newText);
      syncPickerFromInput(newText);
    },
    [syncPickerFromInput]
  );

  const handleInputClick = useCallback(
    (event: ReactMouseEvent<HTMLInputElement>) => {
      // Mobile: if picker is open and user clicks input, close picker and enable text editing
      if (isMobile && isOpen) {
        event.stopPropagation();

        if (selectedDate) {
          let currentValue: Date;
          if (type === 'datetime') {
            const secondsToUse = showSecond ? selectedSecond : 0;
            currentValue = dateOps.makeDate(
              dateOps.getYear(selectedDate),
              dateOps.getMonth(selectedDate),
              dateOps.getDate(selectedDate),
              selectedHour,
              selectedMinute,
              secondsToUse
            );
          } else {
            currentValue = new Date(selectedDate);
          }
          onChangeRef.current?.(currentValue);
        }

        setInputText(selectedDate ? formatDate(selectedDate) : '');
        onEnteringTextModeRef.current?.();

        setIsOpen(false);
        setView('calendar');
        onOpenChangeRef.current?.(false);

        setIsEditingText(true);
        transitioningToTextModeRef.current = true;

        if (inputRef.current) {
          inputRef.current.blur();
        }

        requestAnimationFrame(() => {
          if (inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
          }
          transitioningToTextModeRef.current = false;
        });
        return;
      }

      // Desktop behavior
      if (!isMobile) {
        if (isOpen && selectedDate) {
          event.stopPropagation();

          let currentValue: Date;
          if (type === 'datetime') {
            const secondsToUse = showSecond ? selectedSecond : 0;
            currentValue = dateOps.makeDate(
              dateOps.getYear(selectedDate),
              dateOps.getMonth(selectedDate),
              dateOps.getDate(selectedDate),
              selectedHour,
              selectedMinute,
              secondsToUse
            );
          } else {
            currentValue = new Date(selectedDate);
          }

          onChangeRef.current?.(currentValue);
          setInputText(currentValue ? formatDate(currentValue) : '');
          setIsEditingText(true);
          return;
        }

        if (!isEditingText && !isOpen) {
          openPicker();
        }
      }
    },
    [
      isMobile,
      isOpen,
      selectedDate,
      type,
      showSecond,
      selectedHour,
      selectedMinute,
      selectedSecond,
      formatDate,
      isEditingText,
      openPicker,
      dateOps,
    ]
  );

  const handleTabKey = useCallback(
    (event: ReactKeyboardEvent<HTMLInputElement>) => {
      parseAndSetDate();
      onKeyDownRef.current?.(event);
    },
    [parseAndSetDate]
  );

  const handleInputKeydown = useCallback(
    (event: ReactKeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Tab') {
        parseAndSetDate();
        // Let default Tab behavior continue
        return;
      }

      if (event.key === 'Enter') {
        event.preventDefault();
        parseAndSetDate();
        (event.target as HTMLInputElement).blur();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        setInputText(value ? formatDate(value) : '');
        setIsEditingText(false);
        (event.target as HTMLInputElement).blur();
      }
    },
    [parseAndSetDate, value, formatDate]
  );

  // ---- calendar navigation ----

  const prevMonth = useCallback((event?: ReactMouseEvent) => {
    if (event) event.stopPropagation();
    setCurrentMonth((m) => {
      if (m === 0) {
        setCurrentYear((y) => y - 1);
        return 11;
      }
      return m - 1;
    });
  }, []);

  const nextMonth = useCallback((event?: ReactMouseEvent) => {
    if (event) event.stopPropagation();
    setCurrentMonth((m) => {
      if (m === 11) {
        setCurrentYear((y) => y + 1);
        return 0;
      }
      return m + 1;
    });
  }, []);

  const openMonthSelector = useCallback((event?: ReactMouseEvent) => {
    if (event) event.stopPropagation();
    setView('month-selector');
  }, []);

  const openYearSelector = useCallback((event?: ReactMouseEvent) => {
    if (event) event.stopPropagation();
    setYearRangeStart(currentYear - 5);
    setView('year-selector');
  }, [currentYear]);

  const selectMonth = useCallback((monthIndex: number, event?: ReactMouseEvent) => {
    if (event) event.stopPropagation();
    setCurrentMonth(monthIndex);
    setView('calendar');
  }, []);

  const selectYear = useCallback((year: number, event?: ReactMouseEvent) => {
    if (event) event.stopPropagation();
    setCurrentYear(year);
    setView('calendar');
  }, []);

  const prevYearRange = useCallback(() => {
    setYearRangeStart((s) => s - 12);
  }, []);

  const nextYearRange = useCallback(() => {
    setYearRangeStart((s) => s + 12);
  }, []);

  // ---- day / time selection ----

  const selectDay = useCallback(
    (day: CalendarDay, event?: ReactMouseEvent) => {
      if (!day.isCurrentMonth || !day.day) return;
      if (event) event.stopPropagation();

      if (type === 'datetime') {
        const newDate = dateOps.makeDate(
          currentYear,
          currentMonth,
          day.day as number,
          selectedHour,
          selectedMinute,
          selectedSecond
        );

        setSelectedDate(newDate);

        // Sync input text if editing
        if (isEditingText) {
          setInputText(formatDate(newDate));
        }

        onChangeRef.current?.(newDate);
        setView('time');

        // Scroll to current time after transition
        requestAnimationFrame(() => {
          scrollToCurrentTime();
        });
      } else {
        const newDate = new Date(Date.UTC(currentYear, currentMonth, day.day as number));
        setSelectedDate(newDate);

        if (isEditingText) {
          setInputText(formatDate(newDate));
        }

        // Confirm immediately for date-only
        onChangeRef.current?.(newDate);
        onConfirmRef.current?.();

        // Close picker
        setIsOpen(false);
        setView('calendar');
        setPortalStyle(HIDDEN_PORTAL_STYLE);
        onOpenChangeRef.current?.(false);
        onCloseRef.current?.();
      }
    },
    [
      type,
      currentYear,
      currentMonth,
      selectedHour,
      selectedMinute,
      selectedSecond,
      isEditingText,
      formatDate,
      scrollToCurrentTime,
      dateOps,
    ]
  );

  const selectTodayHandler = useCallback(
    (event?: ReactMouseEvent) => {
      if (event) event.stopPropagation();

      const today = new Date();

      if (type === 'datetime') {
        // "Today" in the operating zone — reads month/year/time-of-day in zone, not browser-local.
        setCurrentMonth(dateOps.getMonth(today));
        setCurrentYear(dateOps.getYear(today));
        setSelectedDate(today);
        setSelectedHour(dateOps.getHours(today));
        setSelectedMinute(dateOps.getMinutes(today));
        setSelectedSecond(dateOps.getSeconds(today));

        if (isEditingText) {
          setInputText(formatDate(today));
        }

        onChangeRef.current?.(today);
        setView('time');

        requestAnimationFrame(() => {
          scrollToCurrentTime();
        });
      } else {
        // Date-only path — unchanged. Today is taken as the browser-local date and pinned to UTC.
        setCurrentMonth(today.getMonth());
        setCurrentYear(today.getFullYear());
        setSelectedDate(today);
        const utcToday = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));

        if (isEditingText) {
          setInputText(formatDate(utcToday));
        }

        onChangeRef.current?.(utcToday);
        onConfirmRef.current?.();

        setIsOpen(false);
        setView('calendar');
        setPortalStyle(HIDDEN_PORTAL_STYLE);
        onOpenChangeRef.current?.(false);
        onCloseRef.current?.();
      }
    },
    [type, scrollToCurrentTime, isEditingText, formatDate, dateOps]
  );

  const clearValue = useCallback((event?: ReactMouseEvent) => {
    if (event) event.stopPropagation();
    onChangeRef.current?.(null);
    onClearRef.current?.();
    closePickerRef.current();
  }, []);

  // Apply a new hour/minute/second to the selected date. The new date is computed here
  // rather than inside a setSelectedDate updater: updaters can run during render, and
  // calling onChange (a parent setState) from one updates the parent mid-render.
  const applyTimePart = useCallback((hour: number, minute: number, second: number) => {
    const prev = selectedDateRef.current;
    if (!prev) return;
    const updated = dateOps.makeDate(
      dateOps.getYear(prev),
      dateOps.getMonth(prev),
      dateOps.getDate(prev),
      hour,
      minute,
      second
    );
    // Keep the refs current so a second tap before the next commit builds on this one
    selectedDateRef.current = updated;
    setSelectedDate(updated);

    // Sync input text if editing
    if (isEditingTextRef.current) {
      setInputText(formatDate(updated));
    }

    onChangeRef.current?.(updated);
  }, [formatDate, dateOps]);

  const selectHourHandler = useCallback((hour: number) => {
    selectedHourRef.current = hour;
    setSelectedHour(hour);
    applyTimePart(hour, selectedMinuteRef.current, selectedSecondRef.current);

    requestAnimationFrame(() => {
      scrollSelectedToCenter('hour');
    });
  }, [applyTimePart, scrollSelectedToCenter]);

  const selectMinuteHandler = useCallback((minute: number) => {
    selectedMinuteRef.current = minute;
    setSelectedMinute(minute);
    applyTimePart(selectedHourRef.current, minute, selectedSecondRef.current);

    requestAnimationFrame(() => {
      scrollSelectedToCenter('minute');
    });
  }, [applyTimePart, scrollSelectedToCenter]);

  const selectSecondHandler = useCallback((second: number) => {
    selectedSecondRef.current = second;
    setSelectedSecond(second);
    applyTimePart(selectedHourRef.current, selectedMinuteRef.current, second);

    requestAnimationFrame(() => {
      scrollSelectedToCenter('second');
    });
  }, [applyTimePart, scrollSelectedToCenter]);

  const backToCalendar = useCallback(() => {
    setView('calendar');
  }, []);

  // ---- render helpers ----

  const renderCalendarView = () => (
    <div className="datetime-picker-calendar">
      <div className="calendar-header">
        <button className="calendar-nav-btn" onClick={prevMonth}>
          <i className="fas fa-chevron-left" />
        </button>
        <div className="calendar-month-year">
          <span className="month-selector-trigger" onClick={openMonthSelector}>
            {monthName}
          </span>
          <span className="year-selector-trigger" onClick={openYearSelector}>
            {currentYear}
          </span>
        </div>
        <button className="calendar-nav-btn" onClick={nextMonth}>
          <i className="fas fa-chevron-right" />
        </button>
      </div>

      <div className="calendar-weekdays">
        {WEEKDAY_LABELS.map((day) => (
          <div key={day} className="calendar-weekday">
            {day}
          </div>
        ))}
      </div>

      <div className="calendar-days">
        {calendarDays.map((day, index) => {
          const classes = ['calendar-day'];
          if (isToday(day)) classes.push('is-today');
          if (isSelectedDay(day)) classes.push('is-selected');
          if (!day.isCurrentMonth) classes.push('is-other-month');

          return (
            <div
              key={index}
              className={classes.join(' ')}
              onClick={(e) => selectDay(day, e)}
            >
              {day.day}
            </div>
          );
        })}
      </div>

      <div className="calendar-footer">
        <button className="calendar-footer-btn" onClick={selectTodayHandler}>
          Today
        </button>
        {clearable && (
          <button className="calendar-footer-btn" onClick={clearValue}>
            Clear
          </button>
        )}
      </div>
    </div>
  );

  const renderMonthSelector = () => (
    <div className="datetime-picker-month-selector">
      <div className="month-selector-header">
        <button className="calendar-nav-btn" onClick={() => setView('calendar')}>
          <i className="fas fa-chevron-left" />
        </button>
        <div className="month-selector-year">
          <span className="year-selector-trigger" onClick={openYearSelector}>
            {currentYear}
          </span>
        </div>
        <div className="month-selector-spacer" />
      </div>

      <div className="month-grid">
        {MONTH_NAMES_SHORT.map((month, index) => {
          const classes = ['month-item'];
          if (isCurrentMonthHighlight(index)) classes.push('is-current');
          if (isSelectedMonthHighlight(index)) classes.push('is-selected');

          return (
            <div
              key={index}
              className={classes.join(' ')}
              onClick={(e) => selectMonth(index, e)}
            >
              {month}
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderYearSelector = () => (
    <div className="datetime-picker-year-selector">
      <div className="year-selector-header">
        <button className="calendar-nav-btn" onClick={prevYearRange}>
          <i className="fas fa-chevron-left" />
        </button>
        <div className="year-selector-range">
          {yearRangeStart} - {yearRangeStart + 11}
        </div>
        <button className="calendar-nav-btn" onClick={nextYearRange}>
          <i className="fas fa-chevron-right" />
        </button>
      </div>

      <div className="year-grid">
        {yearRange.map((year) => {
          const classes = ['year-item'];
          if (isCurrentYearHighlight(year)) classes.push('is-current');
          if (isSelectedYearHighlight(year)) classes.push('is-selected');

          return (
            <div
              key={year}
              className={classes.join(' ')}
              onClick={(e) => selectYear(year, e)}
            >
              {year}
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderTimeView = () => (
    <div className="datetime-picker-time">
      <div className="time-header">
        <button className="time-back-btn" onClick={backToCalendar}>
          <i className="fas fa-chevron-left" /> Back
        </button>
        <div className="time-date-display">{formatDate(selectedDate)}</div>
      </div>

      <div className="time-columns">
        <div className="time-column time-column-hour">
          <div className="column-header">Hour</div>
          <div className="column-list">
            {HOURS_24.map((hour) => (
              <div
                key={'h' + hour}
                className={
                  'column-item' + (hour === selectedHour ? ' column-item--selected' : '')
                }
                onClick={() => selectHourHandler(hour)}
              >
                {pad(hour)}
              </div>
            ))}
          </div>
        </div>

        <div className="time-column time-column-minute">
          <div className="column-header">Min</div>
          <div className="column-list">
            {MINUTES_60.map((minute) => (
              <div
                key={'m' + minute}
                className={
                  'column-item' +
                  (minute === selectedMinute ? ' column-item--selected' : '')
                }
                onClick={() => selectMinuteHandler(minute)}
              >
                {pad(minute)}
              </div>
            ))}
          </div>
        </div>

        {showSecond && (
          <div className="time-column time-column-second">
            <div className="column-header">Sec</div>
            <div className="column-list">
              {SECONDS_60.map((second) => (
                <div
                  key={'s' + second}
                  className={
                    'column-item' +
                    (second === selectedSecond ? ' column-item--selected' : '')
                  }
                  onClick={() => selectSecondHandler(second)}
                >
                  {pad(second)}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderPortalContent = () => {
    if (view === 'calendar') return renderCalendarView();
    if (view === 'month-selector') return renderMonthSelector();
    if (view === 'year-selector') return renderYearSelector();
    if (view === 'time') return renderTimeView();
    return null;
  };

  // ---- main render ----

  const handlePortalMouseDown = useCallback((e: ReactMouseEvent) => {
    // Prevent clicks inside the portal from blurring the input.
    // Without this, clicking time column items triggers input blur first,
    // which runs parseAndSetDate and causes state updates that interfere
    // with the subsequent click event on the time item.
    e.preventDefault();
  }, []);

  const portalElement = isOpen && appendToBody ? (
    <div
      ref={portalRef}
      className={'datetime-picker-portal' + (portalClasses ? ' ' + portalClasses : '')}
      style={portalStyle}
      onMouseDown={handlePortalMouseDown}
    >
      {renderPortalContent()}
    </div>
  ) : null;

  return (
    <>
      <div className={wrapperClass}>
        <div
          className="datetime-picker-control"
          ref={controlRef}
          onClick={handleControlClick}
          onKeyDown={handleKeydown}
        >
          <input
            ref={inputRef}
            type="text"
            className="datetime-picker-input"
            value={displayValue}
            disabled={disabled}
            readOnly={isInputReadonly}
            onTouchStart={handleInputMousedown}
            onMouseDown={handleInputMousedown}
            onClick={handleInputClick}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onKeyDown={handleInputKeydown}
            {...inputAttr}
          />
        </div>
      </div>
      {portalElement && createPortal(portalElement, document.body)}
    </>
  );
});

(window as any).DateTimePickerReact = DateTimePicker;
